import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import LoginBgImage from "../assets/login-bg.jpg";
import GirlImg from "../assets/girl.jpg";
import Logo from "../assets/logo.jpg";
import { login, forgotPassword } from "../../services/authService";
import { storeAdminToken, isAdminLoggedIn, removeAdminToken } from "../../services/localStorage";
import { toast } from "react-toastify";

const Login = ({ onLoginSuccess }) => {
  const [isForgot, setIsForgot] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });

  const [errors, setErrors] = useState({});

  const navigate = useNavigate();

  /* =========================
      RESPONSIVE
  ========================= */

  const isMobile = useMediaQuery({ maxWidth: 768 });

  const isTablet = useMediaQuery({
    minWidth: 769,
    maxWidth: 1024
  });

  /* =========================
      FORM HANDLERS
  ========================= */

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: ""
    }));
  };

  const validateForm = () => {
    let newErrors = {};

    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Enter valid email";
    }

    if (!isForgot) {
      if (!formData.password) {
        newErrors.password = "Password is required";
      } else if (formData.password.length < 4) {
        newErrors.password = "Minimum 4 characters required";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {

    e.preventDefault();

    if (!validateForm()) return;

    // FORGOT PASSWORD

    if (isForgot) {
      try {
        setLoading(true);
        const data = await forgotPassword({
          email: formData.email,
        });
        // SUCCESS
        if (data.status === 200) {
          toast.success(data.message);
          setIsForgot(false);
          setFormData({
            email: "",
            password: "",
          });

        } else {
          // HANDLE CUSTOM ERROR RESPONSE
          toast.error(data.message);
        }
      } catch (error) {
        toast.error(
          error.response?.data?.message ||
          "Failed to send reset link"
        );
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      removeAdminToken();
      const data = await login({
        email: formData.email,
        password: formData.password,
      });

      console.log(data);

      // SUCCESS
      if (data.access_token) {
        storeAdminToken(data.access_token);
        toast.success("Login successful");

        // ROLE CHECK
        if (data.role === "Admin") {

          navigate("/admin", { replace: true });

        } else {

          toast.error("Unauthorized role");
        }

      } else {

        toast.error(data.message || "Login failed");
      }

    } catch (error) {
      console.error(error);
      
      // REMOVE OLD TOKEN
      removeAdminToken();
      toast.error(
        error.response?.data?.message ||
        "Invalid email or password"
      );
    }
  };

  if (isAdminLoggedIn()) {
    return <Navigate to="/admin" replace />;
  }

  return (
    <div
      className="login-page"
      style={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "#e3e3e3",
        fontFamily: "Arial, sans-serif",
        padding: isMobile ? "90px 10px 20px" : "30px",
        position: "relative",
        overflowX: "hidden"
      }}
    >
      {/* HEADER */}

      <div
        className="login-header"
        style={{
          position: "absolute",
          top: isMobile ? "250px" : "40px",
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          gap: isMobile ? "6px" : "12px",
          width: isMobile ? "92%" : "95%",
          textAlign: "center"
        }}
      >
        <img
          src={Logo}
          alt="logo"
          style={{
            height: isMobile ? "24px" : "50px",
            width: isMobile ? "24px" : "50px",
            objectFit: "contain"
          }}
        />

        <h2
          style={{
            fontSize: isMobile ? "13px" : isTablet ? "24px" : "30px",
            color: "#2f66b1",
            margin: 0,
            fontWeight: "500",
            lineHeight: isMobile ? "18px" : "normal"
          }}
        >
          Welcome to Sharada Hill Modern School
        </h2>
      </div>

      {/* LOGIN CONTAINER */}

      <div
        className="login-container"
        style={{
          width: isMobile ? "92%" : isTablet ? "85%" : "760px",
          minHeight: isMobile ? "260px" : isTablet ? "360px" : "430px",
          display: "flex",
          flexDirection: "row",
          borderRadius: "12px",
          overflow: "visible",
          boxShadow: "0 15px 40px rgba(0,0,0,0.15)",
          position: "relative",
          background: "#fff",
          paddingRight: 0,
        }}
      >
        {/* LEFT SIDE */}

        <div
          className="left-side"
          style={{
            width: "48%",
            background: "#f5f5f5",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: isMobile ? "10px" : "20px"
          }}
        >
          <img
            src={LoginBgImage}
            alt="School"
            style={{
              width: "100%",
              maxWidth: isMobile
                ? "120px"
                : isTablet
                  ? "180px"
                  : "260px",
              objectFit: "contain"
            }}
          />
        </div>

        {/* RIGHT SIDE */}

        <div
          className="right-side"
          style={{
            width: "52%",
            background: "white",
            padding: isMobile ? "15px" : "30px",
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            position: "relative",
            overflow: "visible"
          }}
        >
          {/* BACK BUTTON */}

          <button
            onClick={() => navigate("/")}
            style={{
              position: "absolute",
              top: isMobile ? "8px" : "15px",
              right: isMobile ? "10px" : "20px",
              background: "transparent",
              border: "none",
              color: "#2f66b1",
              fontSize: isMobile ? "10px" : "14px",
              cursor: "pointer",
              fontWeight: "bold"
            }}
          >
            ← Back
          </button>

          {/* LOGIN BOX */}

          <div
            className="login-box"
            style={{
              width: "100%",
              maxWidth: isMobile
                ? "180px"
                : isTablet
                  ? "280px"
                  : "320px",
              marginRight: isMobile ? "50px" : isTablet ? "110px" : "170px",
              zIndex: 2
            }}
          >
            <h2
              style={{
                marginBottom: "18px",
                color: "#5f5fff",
                fontSize: isMobile
                  ? "10px"
                  : isTablet
                    ? "24px"
                    : "15px",
                fontWeight: "50px",
                letterSpacing: "1px"
              }}
            >
              {loading ? "Sending..." : isForgot ? "Send Reset Link" : "Log in"}
            </h2>

            <form onSubmit={handleSubmit}>
              {/* EMAIL */}

              <input
                type="email"
                name="email"
                placeholder="Email Address"
                value={formData.email}
                onChange={handleChange}
                style={{
                  width: "150%",
                  padding: isMobile ? "8px" : "14px",
                  marginBottom: "12px",
                  borderRadius: "6px",
                  border: errors.email
                    ? "1px solid red"
                    : "1px solid #d6d6d6",
                  fontSize: isMobile ? "11px" : "15px",
                  boxSizing: "border-box",
                  outline: "none"
                }}
              />

              {errors.email && (
                <p
                  style={{
                    color: "red",
                    fontSize: "12px",
                    marginTop: "-5px"
                  }}
                >
                  {errors.email}
                </p>
              )}

              {/* PASSWORD */}

              {!isForgot && (
                <>
                  <input
                    type="password"
                    name="password"
                    placeholder="Password"
                    value={formData.password}
                    onChange={handleChange}
                    style={{
                      width: "150%",
                      padding: isMobile ? "8px" : "14px",
                      marginBottom: "12px",
                      borderRadius: "6px",
                      border: errors.password
                        ? "1px solid red"
                        : "1px solid #d6d6d6",
                      fontSize: isMobile ? "11px" : "15px",
                      boxSizing: "border-box",
                      outline: "none"
                    }}
                  />

                  {errors.password && (
                    <p
                      style={{
                        color: "red",
                        fontSize: "12px",
                        marginTop: "-5px"
                      }}
                    >
                      {errors.password}
                    </p>
                  )}
                </>
              )}

              {/* FORGOT PASSWORD */}

              {!isForgot && (
                <p
                  onClick={() => setIsForgot(true)}
                  style={{
                    textAlign: "right",
                    cursor: "pointer",
                    color: "#5f5fff",
                    fontSize: isMobile ? "10px" : "14px",
                    marginBottom: "18px",
                    width: "150%"
                  }}
                >
                  Forgot Password?
                </p>
              )}

              {/* BUTTON */}
              <div
                style={{
                  width: "150%",
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "10px"
                }}
              >
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    width: isMobile ? "100px" : "140px",
                    padding: isMobile ? "9px" : "12px",
                    background:
                      "linear-gradient(90deg, #5f5fff, #7a5cff)",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: loading ? "not-allowed" : "pointer",
                    fontSize: isMobile ? "8px" : "12px",
                    fontWeight: "500",
                    opacity: loading ? 0.7 : 1
                  }}
                >
                  {
                    loading
                      ? "Sending..."
                      : isForgot
                        ? "Send Reset Link"
                        : "Log in"
                  }
                </button>
              </div>
            </form>
          </div>

          {/* GIRL IMAGE */}

          <img
            src={GirlImg}
            alt="girl"
            style={{
              position: "absolute",
              right: isMobile
                ? "-34px"
                : isTablet
                  ? "-80px"
                  : "-140px",
              bottom: 0,
              height: isMobile
                ? "120px"
                : isTablet
                  ? "260px"
                  : "390px",
              width: "auto",
              objectFit: "contain",
              zIndex: 1,
              pointerEvents: "none"
            }}
          />
        </div>
      </div>
    </div>
  );
};


export default Login;